<template>
  <div :class="$style.container">
    <SettingsPagePanel v-if="policy">
      <SettingsPagePanelHeader>
        <template #title>アクセスコントロール編集</template>
        <template #right>
          <div :class="$style.buttonGroup">
            <FlatButton variant="secondary" size="medium" @click="handleCancel"
              >キャンセル
            </FlatButton>
            <FlatButton
              variant="primary"
              size="medium"
              @click="handleSave"
              :disabled="isButtonDisabled"
            >
              保存
            </FlatButton>
          </div>
        </template>
      </SettingsPagePanelHeader>
      <SettingsPageAuthzPolicyForm v-model="form" />
    </SettingsPagePanel>
  </div>
</template>

<script lang="ts" setup>
import { ref, computed, watchEffect } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { captureException as newrelicCaptureException } from '@/newrelic'

import { useAlert } from '@/libs/alertPortal'
import FlatButton from '@/components/atoms/FlatButton.vue'

import SettingsPageAuthzPolicyForm, {
  formatToGQLInput,
  type AuthzPolicyForm,
} from './Form/SettingsPageAuthzPolicyForm.vue'
import SettingsPagePanel from '../SettingsPagePanel.vue'
import SettingsPagePanelHeader from '../SettingsPagePanelHeader.vue'
import {
  useSettingsPageAuthzPolicyEditViewQuery,
  useUpdateAuthzPolicyMutation,
} from './SettingsPageAuthzPolicyEditView.generated'

const alert = useAlert()
const router = useRouter()
const route = useRoute('authzPolicySettingDetail')
const authzPolicyGuid = route.params.authzPolicySettingGuid
const { result } = useSettingsPageAuthzPolicyEditViewQuery(
  {
    guid: authzPolicyGuid,
  },
  {
    fetchPolicy: 'no-cache',
  }
)
const { mutate } = useUpdateAuthzPolicyMutation()

const entranceGUID = computed(() => route.params.entranceGUID)

const form = ref<AuthzPolicyForm>({
  name: '',
  effect: 'PERMIT',
  principal: {
    type: 'AnyAuthzPolicyPrincipal',
  },
  action: {
    type: 'AnyAuthzPolicyAction',
  },
  resource: {
    type: 'AnyAuthzPolicyResource',
  },
})

watchEffect(() => {
  if (!result.value?.authzPolicy) {
    return
  }

  form.value.name = result.value.authzPolicy.name
  form.value.effect = result.value.authzPolicy.effect

  switch (result.value.authzPolicy.principal.__typename) {
    case 'AnyAuthzPolicyPrincipal':
      form.value.principal = {
        type: 'AnyAuthzPolicyPrincipal',
      }
      break
    case 'StaffAuthzPolicyPrincipal':
      form.value.principal = {
        type: 'StaffAuthzPolicyPrincipal',
        id: result.value.authzPolicy.principal.staffGUID,
      }
      break
    case 'TeamAuthzPolicyPrincipal':
      form.value.principal = {
        type: 'TeamAuthzPolicyPrincipal',
        id: result.value.authzPolicy.principal.teamGUID,
      }
      break
  }

  switch (result.value.authzPolicy.action.__typename) {
    case 'AnyAuthzPolicyAction':
      form.value.action = {
        type: 'AnyAuthzPolicyAction',
      }
      break
    case 'ReadRoomAuthzPolicyAction':
      form.value.action = {
        type: 'ReadRoomAuthzPolicyAction',
        isAssigned: result.value.authzPolicy.action.isAssigned,
      }
      break
  }

  switch (result.value.authzPolicy.resource.__typename) {
    case 'AnyAuthzPolicyResource':
      form.value.resource = {
        type: 'AnyAuthzPolicyResource',
      }
      break
    case 'RoomAuthzPolicyResource':
      form.value.resource = {
        type: 'RoomAuthzPolicyResource',
        id: result.value.authzPolicy.resource.roomID,
      }
      break
  }
})

const policy = computed(() => {
  return result.value?.authzPolicy
})

const handleCancel = () => {
  // eslint-disable-next-line fp/no-mutating-methods
  router.push({
    name: 'authzPolicySettingDetail',
    params: {
      authzPolicySettingGuid: authzPolicyGuid,
      entranceGUID: entranceGUID.value,
    },
  })
}

const isSaving = ref(false)
const isButtonDisabled = computed(() => {
  // TODO: formがvalidかどうかの判定を追加する
  return isSaving.value
})

const handleSave = async () => {
  isSaving.value = true

  const input = {
    guid: authzPolicyGuid,
    ...formatToGQLInput(form.value),
  }

  await mutate({ input })
    .then(() => {
      // eslint-disable-next-line fp/no-mutating-methods
      router.push({
        name: 'authzPolicySetting',
      })
    })
    .catch(e => {
      newrelicCaptureException(e)
      alert.error(
        'エラーが発生しました。再度お試しいただくか、サポートにお問い合わせください。'
      )
    })
    .finally(() => {
      isSaving.value = false
    })
}
</script>

<style lang="scss" module>
.container {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.buttonGroup {
  display: flex;
  gap: 16px;
  justify-content: flex-end;
}
</style>
